import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, X, Plus, Pencil, Trash2, Image as ImageIcon, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Button, Input, Select, Card, CardContent, Badge, cn } from '../../ui';
import ConfirmacionEliminar from '../../ui/ConfirmacionEliminar';
import { getAreas, updateArea, deleteArea, imagenAreaUrl } from '../api';
import useImagenBlob from '../../../../funciones/useImagenBlob';
import { useFormAuth } from '../../laeformacion/FormacionAccess';
import SepeAreaDialog from './SepeAreaDialog';

const SEPE_THEME = { '--brand-dark': '21 90% 42%', '--brand-light': '25 95% 53%' };

// Miniatura con descarga autenticada del blob (hook por fila).
function AreaThumb({ imagen }) {
  const src = useImagenBlob(imagen ? imagenAreaUrl(imagen) : null);
  return (
    <div className="w-12 h-12 rounded-md border border-slate-200 bg-slate-50 overflow-hidden grid place-items-center">
      {src ? <img src={src} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 text-slate-300" />}
    </div>
  );
}

export default function SepeAreas() {
  const { can } = useFormAuth();
  const canManage = can('formacion.sepe.areas.manage');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [qDeb, setQDeb] = useState('');
  const [fPub, setFPub] = useState('');

  const [dlg, setDlg] = useState({ open: false, modo: 'create', area: null });
  const [delItem, setDelItem] = useState(null);
  const [ordenCursos, setOrdenCursos] = useState(null); // null | 'asc' | 'desc'

  // Debounce de la búsqueda (server-side via ?q=)
  useEffect(() => {
    const t = setTimeout(() => setQDeb(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const cargar = async () => {
    setLoading(true);
    try {
      const params = {};
      if (qDeb.trim()) params.q = qDeb.trim();
      if (fPub !== '') params.publicado_web = fPub;
      const { data } = await getAreas(params);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se pudieron cargar las áreas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [qDeb, fPub]);

  const abrirNueva = () => setDlg({ open: true, modo: 'create', area: null });
  const abrirEditar = (a) => setDlg({ open: true, modo: 'edit', area: a });
  const cerrarDlg = () => setDlg((d) => ({ ...d, open: false }));

  // Tras guardar: recargar lista y cerrar. Ya no se reabre en edit: la imagen
  // se sube en el mismo submit del diálogo (Fase 3).
  const onSaved = () => {
    cargar();
    cerrarDlg();
  };

  // Toggle inline de publicado_web desde el badge (optimistic UI).
  const togglePublicado = async (area) => {
    if (!canManage || area._saving) return;
    const previo = area.publicado_web;
    setItems((prev) => prev.map((a) => (a.id === area.id ? { ...a, publicado_web: !previo, _saving: true } : a)));
    try {
      await updateArea(area.id, { nombre: area.nombre, publicado_web: !previo });
      setItems((prev) => prev.map((a) => (a.id === area.id ? { ...a, _saving: false } : a)));
      toast.success(!previo ? 'Área publicada' : 'Área despublicada');
    } catch (e) {
      setItems((prev) => prev.map((a) => (a.id === area.id ? { ...a, publicado_web: previo, _saving: false } : a)));
      toast.error('No se pudo actualizar');
    }
  };

  const confirmarBorrado = async () => {
    if (!delItem) return;
    try {
      await deleteArea(delItem.id);
      toast.success('Área eliminada');
      setDelItem(null);
      cargar();
    } catch (e) {
      // Error del backend: mostrar el mensaje exacto y dejar el diálogo abierto.
      toast.error(e?.response?.data?.error || 'No se pudo eliminar el área.');
    }
  };

  const limpiar = () => { setQ(''); setFPub(''); };
  const total = items.length;

  // Orden client-side por nº de cursos: null → asc → desc → null.
  const toggleOrdenCursos = () => setOrdenCursos((o) => (o === null ? 'asc' : o === 'asc' ? 'desc' : null));
  const itemsOrdenados = useMemo(() => {
    if (!ordenCursos) return items;
    return [...items].sort((a, b) => {
      const diff = (Number(a.cursos_count) || 0) - (Number(b.cursos_count) || 0);
      return ordenCursos === 'asc' ? diff : -diff;
    });
  }, [items, ordenCursos]);

  return (
    <div className="bg-slate-50 text-slate-900 px-6 py-5 min-h-full" style={SEPE_THEME}>
      {/* Cabecera */}
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 leading-tight">Áreas SEPE</h1>
          <p className="text-sm text-slate-500 mt-1">Cursos SEPE · <span className="font-medium text-slate-700">{total} áreas</span></p>
        </div>
        {canManage && (
          <Button onClick={abrirNueva}><Plus className="w-4 h-4 mr-1.5" /> Nueva área</Button>
        )}
      </div>

      {/* Filtros */}
      <Card className="mb-3">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex-1 min-w-[220px]">
              <Input
                prefix={<Search className="w-4 h-4" />}
                suffix={q ? <button onClick={() => setQ('')}><X className="w-4 h-4" /></button> : null}
                placeholder="Buscar por nombre…"
                value={q} onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <Select className="w-56" value={fPub} onChange={(e) => setFPub(e.target.value)}>
              <option value="">Todas</option>
              <option value="1">Solo publicadas</option>
              <option value="0">Solo no publicadas</option>
            </Select>
            {(q || fPub) && (
              <button onClick={limpiar} className="text-slate-400 hover:text-slate-700 text-sm px-2 py-2 flex items-center gap-1"><X className="w-4 h-4" /> Limpiar</button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide text-left">
              <tr>
                <th className="px-4 py-2.5 w-20">Imagen</th>
                <th className="px-4 py-2.5">Nombre</th>
                <th className="px-4 py-2.5">Slug</th>
                <th className="px-4 py-2.5 w-32">Publicada</th>
                <th className="px-4 py-2.5 w-28">
                  <button type="button" onClick={toggleOrdenCursos} className="inline-flex items-center gap-1 uppercase select-none hover:text-slate-700">
                    Cursos
                    {ordenCursos === 'asc'
                      ? <ChevronUp className="w-3 h-3" />
                      : ordenCursos === 'desc'
                        ? <ChevronDown className="w-3 h-3" />
                        : <ChevronsUpDown className="w-3 h-3 opacity-40" />}
                  </button>
                </th>
                {canManage && <th className="px-4 py-2.5 w-24 text-right">Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={canManage ? 6 : 5} className="px-4 py-10 text-center text-slate-400">Cargando…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={canManage ? 6 : 5} className="px-4 py-12 text-center">
                    <p className="text-slate-400 mb-3">Aún no hay áreas. Crea la primera.</p>
                    {canManage && <Button onClick={abrirNueva}><Plus className="w-4 h-4 mr-1.5" /> Nueva área</Button>}
                  </td>
                </tr>
              )}
              {!loading && itemsOrdenados.map((a) => (
                <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2.5"><AreaThumb imagen={a.imagen} /></td>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{a.nombre}</td>
                  <td className="px-4 py-2.5"><span className="font-mono text-xs text-slate-400">{a.slug}</span></td>
                  <td className="px-4 py-2.5">
                    {canManage ? (
                      <button
                        type="button"
                        onClick={() => togglePublicado(a)}
                        disabled={a._saving}
                        aria-label={a.publicado_web ? 'Despublicar área' : 'Publicar área'}
                        className="cursor-pointer hover:opacity-80 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {a.publicado_web
                          ? <Badge variant="success">Sí</Badge>
                          : <Badge variant="secondary">No</Badge>}
                      </button>
                    ) : (
                      a.publicado_web
                        ? <Badge variant="success">Sí</Badge>
                        : <Badge variant="secondary">No</Badge>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <Link
                      to={`/formacion/sepe/cursos?area=${a.id}`}
                      className={cn(
                        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                        Number(a.cursos_count) > 0
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200',
                      )}
                    >
                      {a.cursos_count ?? 0}
                    </Link>
                  </td>
                  {canManage && (
                    <td className="px-4 py-2.5 text-right">
                      <button className="text-slate-400 hover:text-slate-700 mr-2" title="Editar" onClick={() => abrirEditar(a)}><Pencil className="w-4 h-4" /></button>
                      <button className="text-slate-400 hover:text-rose-500" title="Eliminar" onClick={() => setDelItem(a)}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Crear / editar */}
      <SepeAreaDialog
        open={dlg.open}
        modo={dlg.modo}
        area={dlg.area}
        onClose={cerrarDlg}
        onSaved={onSaved}
      />

      {/* Confirmación de borrado (word-gate: escribir ELIMINAR). Copy dinámico
          según nº de cursos que quedarán sin vinculación. */}
      <ConfirmacionEliminar
        open={!!delItem}
        onClose={() => setDelItem(null)}
        onConfirm={confirmarBorrado}
        titulo="Eliminar área"
        descripcion={
          delItem
            ? (Number(delItem.cursos_count) > 0
                ? `Se eliminará el área "${delItem.nombre}". ${Number(delItem.cursos_count)} curso(s) asignados quedarán sin vinculación con esta área. Esta acción no se puede deshacer.`
                : `Se eliminará el área "${delItem.nombre}". Esta acción no se puede deshacer.`)
            : ''
        }
        textoBotonConfirmar="Eliminar área"
      />

      <ToastContainer position="bottom-right" autoClose={3000} />
    </div>
  );
}
